<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="480" :title="$t('common.assign_evaluation')" @input="onInput"
            @close="hide">
            <div class="eval-option-list">
                <button v-for="evaluation in evaluations" :key="evaluation.id" type="button" class="eval-option"
                    :class="{ active: isActive(evaluation) }" @click="selectedEvaluation = evaluation">
                    <span class="eval-option-name">{{ evaluation.name }}</span>
                    <v-icon v-if="isActive(evaluation)" size="18" class="eval-option-check">mdi-check-circle</v-icon>
                </button>
                <div v-if="evaluations.length === 0" class="eval-option-empty">
                    {{ $t('common.not_found') }}
                </div>
            </div>

            <template #footer>
                <dialog-actions :confirm-label="$t('common.assign')" :disabled="disabled" :loading="loading"
                    @cancel="hide" @confirm="assign" />
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import BaseDialog from '@/components/common/BaseDialog';
import DialogActions from '@/components/common/DialogActions';

export default {
    name: "AssignEvaluationDialog",
    components: { BaseDialog, DialogActions },
    props: {
        type: {
            type: String,
            default: 'student'
        },
        coachId: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            currentId: null,
            selectedEvaluation: null,
        }
    },
    computed: {
        ...mapState(['evaluations']),
        disabled(vm = this) {
            return !vm.selectedEvaluation
        },
        collection(vm = this) {
            if (vm.type === 'group') return 'groups'
            if (vm.type === 'team') return 'teams'
            return 'users'
        },
        currentAttribute(vm = this) {
            if (vm.type === 'group') return 'groupId'
            if (vm.type === 'team') return 'teamId'
            return 'studentId'
        },
    },
    async mounted() {
        const vm = this
        vm.loadEvaluations()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        show(currentId) {
            const vm = this
            vm.currentId = currentId
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.currentId = null
            vm.selectedEvaluation = null
        },
        async loadEvaluations() {
            const vm = this;
            if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) await vm.bindEvaluations(vm.coachId)
        },
        isActive(evaluation) {
            const vm = this
            return vm.selectedEvaluation?.id === evaluation.id
        },
        async assign() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    coachId: vm.coachId || vm.currentUser.uid,
                    evaluationId: vm.selectedEvaluation.id,
                    [vm.currentAttribute]: vm.currentId,
                }

                let assignedEvaluation = await db.collection('assignedEvaluations').add(payload)

                await db.collection(vm.collection).doc(vm.currentId).update({ assignedEvaluationId: assignedEvaluation.id })

                vm.toast(vm.$t('common.assigned_evaluation'))

                vm.$emit('assigned', vm.currentId, assignedEvaluation.id)
                vm.hide()
            })
        }
    },
    watch: {
        coachId() {
            this.loadEvaluations()
        },
    }
}
</script>

<style scoped>
.eval-option-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.eval-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 14px 16px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    background: var(--v-surface-base);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    cursor: pointer;
    text-align: left;
    transition: border-color 0.15s, background 0.15s;
}

.eval-option:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.eval-option.active {
    border-color: var(--v-primary-base);
    background: rgba(204, 255, 51, 0.08);
}

.eval-option.active .eval-option-name {
    color: var(--v-primary-base);
    font-weight: 700;
}

.eval-option-check {
    color: var(--v-primary-base) !important;
    flex-shrink: 0;
}

.theme--light .eval-option.active {
    border-color: var(--v-accent-base);
    background: rgba(51, 153, 153, 0.08);
}

.theme--light .eval-option.active .eval-option-name {
    color: var(--v-accent-base);
}

.theme--light .eval-option-check {
    color: var(--v-accent-base) !important;
}

.eval-option-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}
</style>
